import { DFPModal } from "@/components/modals/dfp-modal";
import { deleteAsset, undeleteAsset } from "@/services/asset-service";
import { deleteSubmission } from "@/services/submission-service";
import { Form, Input, Select, message } from "antd";
import { useState } from "react";

const { TextArea } = Input;

interface DeleteModalProps {
	state: any;
	dispatch: any;
	browserLocationId?: string;
	tableType?: string;
	fetchData?: (
		state: any,
		dispatch: any,
		browserLocationId: string,
	) => Promise<void>;
	onOk?: (params: {
		state: any;
		dispatch: any;
		setConfirmLoading: (loading: boolean) => void;
		browserLocationId?: string;
		fetchData?: (
			state: any,
			dispatch: any,
			browserLocationId: string,
		) => Promise<void>;
	}) => Promise<void>;
}

export const DeleteModal = (props: DeleteModalProps) => {
	const {
		state,
		dispatch,
		browserLocationId,
		fetchData,
		onOk: customOnOk,
	} = props;
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [deletionReason, setDeletionReason] = useState<string | undefined>(
		undefined,
	);
	const [deletionNotes, setDeletionNotes] = useState<string>("");

	const onOk = async () => {
		setConfirmLoading(true);
		let shouldRefresh = false;

		try {
			if (customOnOk) {
				await customOnOk({
					state,
					dispatch,
					setConfirmLoading,
					browserLocationId,
					fetchData,
				});
				shouldRefresh = true;
			} else {
				// Default onOk implementation
				if (state?.checkedItems?.length) {
					// Determine which function to use based on tableType and viewingDeleted state
					const isUndelete =
						state.viewingDeleted && props.tableType === "assets";

					if (props.tableType === "assets") {
						const actionPromises = state.checkedItems.map((item: any) => {
							if (isUndelete) {
								return undeleteAsset(item)
									.then(() => ({ item, success: true }))
									.catch((error) => ({ item, success: false, error }));
							} else {
								return deleteAsset(item, deletionReason, deletionNotes)
									.then(() => ({ item, success: true }))
									.catch((error) => ({ item, success: false, error }));
							}
						});

						const results = await Promise.all(actionPromises);
						const errors = results.filter((result) => !result.success);
						const successes = results.filter((result) => result.success);

						if (successes.length) {
							const actionText = isUndelete ? "undeleted" : "deleted";
							message.success(
								`${successes.length} asset(s) ${actionText} successfully.`,
							);
							shouldRefresh = true;
						}

						if (errors.length) {
							errors.forEach((error) =>
								console.error(
									`Error ${isUndelete ? "undeleting" : "deleting"} item ${error.item}:`,
									error.error,
								),
							);
							const actionText = isUndelete ? "undelete" : "delete";
							message.error(
								`${errors.length} asset(s) failed to ${actionText}.`,
							);
							// Still refresh even if some failed, to show current state
							if (successes.length > 0) {
								shouldRefresh = true;
							}
						}
					} else {
						// Submissions table
						const deletePromises = state.checkedItems.map((item: any) =>
							deleteSubmission(item)
								.then(() => ({ item, success: true }))
								.catch((error) => ({ item, success: false, error })),
						);

						const results = await Promise.all(deletePromises);
						const errors = results.filter((result) => !result.success);
						const successes = results.filter((result) => result.success);

						if (successes.length) {
							message.success(
								`${successes.length} items deleted successfully.`,
							);
							shouldRefresh = true;
						}

						if (errors.length) {
							errors.forEach((error) =>
								console.error(
									`Error deleting item ${error.item}:`,
									error.error,
								),
							);
							message.error(`${errors.length} items failed to delete.`);
							// Still refresh even if some failed, to show current state
							if (successes.length > 0) {
								shouldRefresh = true;
							}
						}
					}
				} else {
					const actionText =
						state.viewingDeleted && props.tableType === "assets"
							? "undelete"
							: "delete";
					message.warning(`No items selected for ${actionText}.`);
				}
			}
		} catch (error) {
			console.error("Error during deletion process:", error);
			const actionText =
				state.viewingDeleted && props.tableType === "assets"
					? "undeleting"
					: "deleting";
			message.error(
				`An error occurred while ${actionText} ${props.tableType || "items"}.`,
			);
		} finally {
			// Reset deletion reason and notes
			setDeletionReason(undefined);
			setDeletionNotes("");

			// Close modal first
			// Handle both action formats: AssetsTable uses payload, SubmissionsTable uses value
			if (props.tableType === "assets") {
				dispatch({ type: "SET_SHOW_DELETE_MODAL", payload: false });
			} else {
				dispatch({ type: "SET_SHOW_DELETE_MODAL", value: false });
			}

			// Reset checked items
			dispatch({ type: "SET_CHECKED_ITEMS", payload: [] });

			// Refresh the page if we had successful deletions
			if (shouldRefresh) {
				window.location.reload();
			}

			setConfirmLoading(false);
		}
	};

	const onCancel = () => {
		// Reset deletion reason and notes
		setDeletionReason(undefined);
		setDeletionNotes("");

		// Handle both action formats: AssetsTable uses payload, SubmissionsTable uses value
		if (props.tableType === "assets") {
			dispatch({ type: "SET_SHOW_DELETE_MODAL", payload: false });
		} else {
			dispatch({ type: "SET_SHOW_DELETE_MODAL", value: false });
		}
	};
	const isDeleting = !state.viewingDeleted && props.tableType === "assets";

	return (
		<DFPModal
			open={state.showDeleteModal}
			onCancel={onCancel}
			onOk={onOk}
			confirmLoading={confirmLoading}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
				<span>
					{state.viewingDeleted && props.tableType === "assets"
						? `Are you sure you want to undelete these ${props?.tableType || "records"}?`
						: `Are you sure you want to delete these ${props?.tableType || "records"}?`}
				</span>

				{isDeleting && (
					<Form layout="vertical">
						<Form.Item label="Deletion Reason" style={{ marginBottom: "12px" }}>
							<Select
								placeholder="Select a reason (optional)"
								allowClear
								value={deletionReason}
								onChange={(value) => {
									setDeletionReason(value);
									if (value !== "Error") {
										setDeletionNotes("");
									}
								}}
								options={[
									{ label: "Removed", value: "Removed" },
									{ label: "Shelved", value: "Shelved" },
									{ label: "Error", value: "Error" },
								]}
							/>
						</Form.Item>

						{deletionReason === "Error" && (
							<Form.Item label="Additional Notes" style={{ marginBottom: 0 }}>
								<TextArea
									placeholder="Please provide details about the error..."
									value={deletionNotes}
									onChange={(e) => setDeletionNotes(e.target.value)}
									rows={3}
								/>
							</Form.Item>
						)}
					</Form>
				)}
			</div>
		</DFPModal>
	);
};
